import type { TFunction } from 'i18next';

import type { TMetaIntegrationStatus } from '@feathr/blackbox';
import type { TColor } from '@feathr/components/src/style/theme';

/**
 * Maps a Meta integration status to its Pill theme color.
 *
 * @param status - The backend-derived Meta integration status.
 * @returns Color token understood by the Pill component.
 */
export function metaStatusColor(status: TMetaIntegrationStatus): string {
  const map: Record<TMetaIntegrationStatus, TColor> = {
    not_connected: 'neutral',
    configuring: 'yellow',
    connected: 'green',
    error: 'red',
    no_active_pages: 'neutral',
  };
  return map[status];
}

/**
 * Maps a Meta integration status to its user-facing badge label.
 *
 * @param t - Translation function.
 * @param status - The backend-derived Meta integration status.
 * @returns Localized short label for the status.
 */
export function metaStatusLabel(t: TFunction, status: TMetaIntegrationStatus): string {
  const map: Record<TMetaIntegrationStatus, string> = {
    not_connected: t('Not connected'),
    configuring: t('Configuring'),
    connected: t('Connected'),
    error: t('Setup failed'),
    no_active_pages: t('No active pages'),
  };
  return map[status];
}

/**
 * Maps a Meta integration status to an optional tooltip shown on the badge.
 *
 * @param t - Translation function.
 * @param status - The backend-derived Meta integration status.
 * @returns Localized tooltip copy, or undefined when no tooltip is needed.
 */
export function metaStatusTooltip(
  t: TFunction,
  status: TMetaIntegrationStatus,
): string | undefined {
  const map: Record<TMetaIntegrationStatus, string | undefined> = {
    not_connected: undefined,
    configuring: t(
      'We will finish setting up your Meta integration shortly. Check back in a few minutes.',
    ),
    connected: undefined,
    error: t('Meta setup failed. See the banner below for details.'),
    no_active_pages: t('No Facebook pages are currently available for this connection.'),
  };
  return map[status];
}
